import { Input } from '../../../shared/components/FormControls';
import Rodal from 'rodal';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { Button } from '../../../shared/components/Button';
import { useNavigate } from 'react-router-dom';
import 'rodal/lib/rodal.css';
import '../../../shared/styles/Toastify.css';

import trialmentLogo from '../../../shared/assets/logos/trialmentlogo.png';
import trialmentTitle from '../../../shared/assets/logos/trialmenttitle.png';
import cotaLogo from '../../../shared/assets/logos/cotalogo.png';
import cotaTitle from '../../../shared/assets/logos/cotatitle.png';
import orchestratorTitle from '../../../shared/assets/logos/orchestratortitle.png';
import orchestratorPlayerTitle from '../../../shared/assets/logos/orchestratorplayertitle.png';
import orchestratorLogo from '../../../shared/assets/logos/orchestratorlogo.png';
import orchestratorLoginBackground from '../../../shared/assets/loginanimations/orchestratorlogin.gif';
import trialmentLoginBackground from '../../../shared/assets/loginanimations/trialmentlogin.gif';
import orchestratorPlayerLoginBackground from '../../../shared/assets/loginanimations/orchestratorplayerlogin.gif';
import cotaLoginBackground from '../../../shared/assets/loginanimations/cotalogin.gif';

import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
} from 'firebase/auth';
import { auth, db, googleProvider } from '../../../firebase/firebaseApp';
import {
  fetchProvisionedUser,
  hasModuleAccess,
} from '../utils/provisioning';
import { matchLegacyCotaAccount } from '../utils/legacyAccounts';
import { Eye, EyeOff, LogIn } from 'lucide-react';

const VALID_SYSTEMS = new Set(['trialment', 'cota', 'orchestrator', 'orchestrator_user']);

const trialmentCampaigns = [
  'Mother Sea',
  'Crimson Sands',
  'Second Hatred',
  'The Starforge',
];

export const LoginModal = (props) => {
  const { system, show, close } = props;

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [loading, setLoading] = useState(false);
  const [backgroundSize, setBackgroundSize] = useState(null);
  const [backgroundPosition, setBackgroundPosition] = useState(null);
  const [backgroundRepeat, setBackgroundRepeat] = useState(null);

  const navigate = useNavigate();

  const requiresCampaignSelection = system === 'trialment';
  const normalizedEmail = useMemo(() => username.trim(), [username]);
  const normalizedPassword = useMemo(() => password.trim(), [password]);

  const isEnterEnabled = useMemo(() => {
    if (loading || !show) return false;
    if (!VALID_SYSTEMS.has(system)) return false;
    if (requiresCampaignSelection && !selectedCampaign) return false;
    return normalizedEmail !== '' && normalizedPassword !== '';
  }, [
    loading,
    show,
    system,
    requiresCampaignSelection,
    selectedCampaign,
    normalizedEmail,
    normalizedPassword,
  ]);

  const isGoogleEnabled = useMemo(() => {
    if (loading || !show) return false;
    if (!VALID_SYSTEMS.has(system)) return false;
    if (requiresCampaignSelection) {
      return selectedCampaign !== null;
    }
    return true;
  }, [loading, show, system, requiresCampaignSelection, selectedCampaign]);

  const getIconClass = (system) => {
    if (system === 'trialment') {
      return 'trialment_login_logo';
    } else if (system === 'cota') {
      return 'cota_login_logo';
    } else if (system === 'orchestrator' || system === 'orchestrator_user') {
      return 'trialment_login_logo';
      //return 'orchestrator_login_logo';
    }
    return '';
  };

  const handleCampaignSelect = (campaign) => {
    if (!loading) {
      setSelectedCampaign(campaign);
    }
  };

  const toggleShowPassword = () => {
    setShowPassword((prev) => !prev);
  };

  const guardAuthAttempt = useCallback(() => {
    if (!VALID_SYSTEMS.has(system)) {
      toast.error('Unsupported module selected.');
      return false;
    }

    if (requiresCampaignSelection && !selectedCampaign) {
      return false;
    }

    return true;
  }, [requiresCampaignSelection, selectedCampaign, system]);

  const resolveProvisionedEmail = useCallback(
    async (candidateEmail) => {
      try {
        const record = await fetchProvisionedUser(db, candidateEmail);
        if (!record) {
          await signOut(auth).catch(() => {});
          toast.info(
            'Thanks for showing interest in Dimension Factory. The modules of Dimension Factory is in beta testing.'
          );
          return null;
        }

        if (!hasModuleAccess(record.data, system)) {
          await signOut(auth).catch(() => {});
          toast.error(
            'You do not have access to this module. Please contact your administrator.'
          );
          return null;
        }

        return { email: record.email, name: record.data?.name ?? '' };
      } catch (error) {
        console.error('Provisioning verification failed', error);
        await signOut(auth).catch(() => {});
        toast.error(
          'We could not verify your account provisioning. Please try again later or reach out to your admin.'
        );
        return null;
      }
    },
    [system]
  );

  const getReadableFirebaseError = (error) => {
    if (!error || !error.code) {
      return 'Unable to sign in. Please try again.';
    }

    switch (error.code) {
      case 'auth/user-not-found':
        return 'No account found for the provided email.';
      case 'auth/wrong-password':
      case 'auth/invalid-credential':
        return `The email, password, or account provider are incorrect, If you've done so in the past, try logging in with Google`;
      case 'auth/too-many-requests':
        return 'Too many attempts. Please try again later.';
      default:
        return 'Failed to authenticate. Please try again.';
    }
  };

  const finalizeLogin = useCallback(
    (email, name = '') => {
      if (!email) {
        return;
      }

      localStorage.setItem('username', email);
      localStorage.setItem('displayName', name || email);
      localStorage.setItem('system', system);
      if (system === 'trialment') {
        localStorage.setItem('campaign', selectedCampaign || '');
      } else {
        localStorage.removeItem('campaign');
      }

      if (typeof close === 'function') {
        close();
      }

      navigate(
        system === 'orchestrator'
          ? '/orchestrator'
          : system === 'orchestrator_user'
          ? '/orchestrator/player'
          : '/campaign'
      );
    },
    [close, navigate, selectedCampaign, system]
  );

  const handleEmailLogin = useCallback(async () => {
    if (!isEnterEnabled || !guardAuthAttempt()) {
      return;
    }

    if (system === 'cota') {
      const legacyAccount = matchLegacyCotaAccount(
        normalizedEmail,
        normalizedPassword
      );

      if (legacyAccount) {
        finalizeLogin(legacyAccount.username);
        return;
      }
    }

    try {
      setLoading(true);

      const credential = await signInWithEmailAndPassword(
        auth,
        normalizedEmail,
        normalizedPassword
      );

      const provisioned = await resolveProvisionedEmail(
        credential.user?.email ?? normalizedEmail
      );

      if (!provisioned) {
        return;
      }

      finalizeLogin(provisioned.email, provisioned.name);
    } catch (error) {
      const message = getReadableFirebaseError(error);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [
    guardAuthAttempt,
    isEnterEnabled,
    resolveProvisionedEmail,
    finalizeLogin,
    normalizedEmail,
    normalizedPassword,
    system,
  ]);

  const handleGoogleLogin = useCallback(async () => {
    if (!isGoogleEnabled || !guardAuthAttempt()) {
      return;
    }

    try {
      setLoading(true);

      const result = await signInWithPopup(auth, googleProvider);
      const provisioned = await resolveProvisionedEmail(result.user?.email);

      if (!provisioned) {
        return;
      }

      finalizeLogin(provisioned.email, provisioned.name);
    } catch (error) {
      if (
        error?.code === 'auth/popup-closed-by-user' ||
        error?.code === 'auth/cancelled-popup-request'
      ) {
        return;
      }

      const message =
        error?.code === 'auth/account-exists-with-different-credential'
          ? 'This email is linked to another sign-in method. Use the associated provider.'
          : 'Google sign-in failed. Please try again.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [guardAuthAttempt, isGoogleEnabled, resolveProvisionedEmail, finalizeLogin]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Enter' && isEnterEnabled) {
        e.preventDefault();
        handleEmailLogin();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleEmailLogin, isEnterEnabled]);

  useEffect(() => {
    if (
      system === 'orchestrator' ||
      system === 'orchestrator_user' ||
      system === 'trialment' ||
      system === 'cota'
    ) {
      setBackgroundSize('cover, cover');
      setBackgroundPosition('center, center');
      setBackgroundRepeat('no-repeat, no-repeat');
    }
    if (!show) {
      setTimeout(() => {
        setUsername('');
        setPassword('');
        setShowPassword(false);
        setSelectedCampaign(null);
        setLoading(false);
        setBackgroundSize(null);
        setBackgroundPosition(null);
        setBackgroundRepeat(null);
      }, 300);
    }
  }, [system, show]);

  return (
    <Rodal
      animation={'fade'}
      visible={show}
      onClose={!loading ? close : () => {}}
      //showMask={false}
      customStyles={{
        border: '2px solid #e4E7C5', //beige
        backgroundColor: '#f6f7eb', //ivory
        // Layered backgrounds: ivory gradient on top of image to simulate ~30% image opacity
        background:
          system === 'orchestrator' ||
          system === 'trialment' ||
          system === 'cota' ||
          system === 'orchestrator_user'
            ? `linear-gradient(rgba(246,247,235,${
                system === 'cota' ? '0.55' : '0.7'
              }), rgba(246,247,235,${
                system === 'cota' ? '0.55' : '0.7'
              })), url(${
                system === 'orchestrator'
                  ? orchestratorLoginBackground
                  : system === 'cota'
                  ? cotaLoginBackground 
                  : system === 'orchestrator_user'
                  ? orchestratorPlayerLoginBackground
                  : trialmentLoginBackground
              })`
            : '#f6f7eb',
        backgroundSize: backgroundSize,
        backgroundPosition: backgroundPosition,
        backgroundRepeat: backgroundRepeat,
      }}
      height={
        system === 'trialment'
          ? 645
          : system === 'orchestrator_user'
          ? 477
          : 465
      }
      width={360}
    >
      <ToastContainer
        position='bottom-center'
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme='colored'
      />
      <div>
        <img
          className={`system_logo ${getIconClass(system)}`}
          src={
            system === 'trialment'
              ? trialmentLogo
              : system === 'cota'
              ? cotaLogo
              : system === 'orchestrator' || system === 'orchestrator_user'
              ? orchestratorLogo
              : ''
          }
          alt=''
        />
      </div>
      <div>
        <div>
          <img
            className={`${ system === 'orchestrator_user' ? 'orchestrator_user_logo_txt' : 'system_logo_txt'} ${
              system === 'orchestrator'
                ? 'trialment_login_logo'
                : //? 'orchestrator_login_logo'
                  'trialment_login_logo'
            }`}
            src={
              system === 'trialment'
                ? trialmentTitle
                : system === 'cota'
                ? cotaTitle
                : system === 'orchestrator' 
                ? orchestratorTitle
                : system === 'orchestrator_user'
                ? orchestratorPlayerTitle
                : ''
            }
            alt=''
          />
        </div>
      </div>
      {system === 'trialment' && (
        <div className='trialment_login_form'>
          <div className='trialment_login_form_row'>
            <>Select Campaign</>
          </div>
          {trialmentCampaigns.map((campaign, index) => (
            <span key={index}>
              {index % 2 === 0 && index + 1 < trialmentCampaigns.length && (
                <div className='trialment_login_form_row' style={{paddingTop: '0px'}}>
                  <span>
                    <Button
                      label={campaign}
                      type={
                        selectedCampaign === campaign
                          ? 'cinnabar'
                          : 'black-white'
                      }
                      onPress={() => handleCampaignSelect(campaign)}
                    />
                  </span>
                  <span className='button_margin'>
                    <Button
                      label={trialmentCampaigns[index + 1]}
                      type={
                        selectedCampaign === trialmentCampaigns[index + 1]
                          ? 'cinnabar'
                          : 'black-white'
                      }
                      onPress={() =>
                        handleCampaignSelect(trialmentCampaigns[index + 1])
                      }
                    />
                  </span>
                </div>
              )}
            </span>
          ))}
        </div>
      )}

      <form className='trialment_login_form'>
        <div className='trialment_login_form_row'>Login</div>
        <div className='trialment_login_form_row trialment_login_form_input_row'>
          <Input
            type='text'
            value={username}
            disabled={loading}
            placeholder='Email'
            onChange={(e) => {
              if (!loading) {
                setUsername(e.target.value);
              }
            }}
            autoComplete='email'
          />
        </div>
        <div
          className='trialment_login_form_row trialment_login_form_input_row'
          style={{ position: 'relative' }}
        >
          <Input
            type={showPassword ? 'text' : 'password'}
            value={password}
            disabled={loading}
            placeholder='Password'
            onChange={(e) => {
              if (!loading) {
                setPassword(e.target.value);
              }
            }}
            autoComplete='current-password'
            style={{ paddingRight: '36px' }}
          />
          <button
            type='button'
            aria-label={showPassword ? 'Hide password' : 'Show password'}
            aria-pressed={showPassword}
            onClick={toggleShowPassword}
            style={{
              position: 'absolute',
              right: '10px',
              top: '60%',
              transform: 'translateY(-50%)',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              padding: 4,
              border: 0,
              background: 'transparent',
              color: 'black',
            }}
          >
            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        </div>
      </form>
      <div style={{ paddingTop: '10px', marginBottom: '10px' }}>
        <Button
          label={'Login with Google'}
          type={
            loading
              ? 'placeholder'
              : isGoogleEnabled
              ? 'blue'
              : 'disabled'
          }
          disabled={!isGoogleEnabled || loading}
          loading={loading}
          onPress={handleGoogleLogin}
        />
        <span className='button_margin'>
          <Button label={'Forgot Password'} type={'disabled'} />
        </span>
      </div>

      <div style={{ paddingTop: '5px', marginBottom: '10px' }}>
        <Button
          icon={LogIn}
          label={'Enter'}
          type={
            loading ? 'placeholder' : isEnterEnabled ? 'cinnabar' : 'disabled'
          }
          disabled={!isEnterEnabled || loading}
          loading={loading}
          onPress={handleEmailLogin}
        />
      </div>
    </Rodal>
  );
};
